import { ConnectionQualityLevels } from "@minervaproject/avsubsystem";
import { isBefore, isValid } from "date-fns";
import { last, range } from "lodash";

import { RootState } from "shared/types";
import {
  AvCheckinMonitoringEventType,
  AvRefreshMonitoringEventType,
  MonitoringEventHistogramDataType,
  MonitoringEventType,
  MonitoringEventTypeType,
  PreClassCheckResultMonitoringEventType,
  PubsubStatsMonitoringEventType,
  SupportActionMonitoringEventType,
  TargetUserMonitoringEventTypeType,
  contextDataKeyType,
  contextDataValueType,
} from "shared/types/monitoring_event";
import { LatestPreclassCheckResultType, PreclassTestResultType } from "shared/types/preclass_check";
import {
  MONITORING_EVENT_TYPES,
  computeMonitoringEventStats,
  getOncePresentUserCount,
} from "shared/utils/monitoring";
import { filterAvRefreshes, filterPreclassChecks } from "utils/monitoring";

function filterEvents(selectedLevels: string[], selectedTypes: string[], selectedUsers: string[]) {
  return (event: MonitoringEventType) => {
    const type = event.type;
    const level = MONITORING_EVENT_TYPES[type]?.level;
    const userId = event.userId;
    if (!selectedLevels.includes(level) && !selectedLevels.includes("all")) return false;
    if (!selectedTypes.includes(type) && !selectedTypes.includes("all")) return false;
    if (!selectedUsers.includes(userId) && !selectedUsers.includes("all")) return false;
    return true;
  };
}

function selectMonitoringEvents(state: RootState) {
  const monitoring_events = Object.values(state.monitoring.monitoring_events);

  const startTimeFilter = state.local_monitoring.start_time_filter;
  const endTimeFilter = state.local_monitoring.end_time_filter;
  const startTimeDate = new Date(startTimeFilter);
  const endTimeDate = new Date(endTimeFilter);

  return monitoring_events.filter(({ timestamp }) => {
    const createdOn = new Date(timestamp);
    if (isValid(startTimeDate) && isBefore(createdOn, startTimeDate)) return false;
    if (isValid(endTimeDate) && isBefore(endTimeDate, createdOn)) return false;
    return true;
  });
}

export const selectMonitoringEventsByType =
  (eventType: MonitoringEventTypeType) => (state: RootState) =>
    selectMonitoringEvents(state).filter((event) => event.type === eventType);

export const selectMonitoringEventsByUserIdAndTypes =
  (userId: string, eventTypes: MonitoringEventTypeType[]) => (state: RootState) =>
    selectMonitoringEvents(state).filter(
      (event) => eventTypes.includes(event.type) && event.userId === userId,
    );

export const selectMonitoringEventsByUserIdAndType =
  (userId: string, eventType: MonitoringEventTypeType) => (state: RootState) =>
    selectMonitoringEventsByUserIdAndTypes(userId, [eventType])(state);

export const selectLatestMonitoringEventByUserIdAndType =
  (userId: string, eventType: MonitoringEventTypeType) => (state: RootState) => {
    const events = selectMonitoringEventsByUserIdAndType(userId, eventType)(state);
    return last(events);
  };

export const selectMonitoringEventCount =
  (selectedUsers: string[], selectedLevels: string[], selectedTypes: string[]) =>
  (state: RootState) =>
    selectMonitoringEvents(state).filter(filterEvents(selectedLevels, selectedTypes, selectedUsers))
      .length;

export const selectMonitoringEventsExistByType =
  (eventType: MonitoringEventTypeType) => (state: RootState) =>
    selectMonitoringEventsByType(eventType)(state).length > 0;

export const selectMonitoringEventCountByUserIdAndType =
  (userId: string, eventType: MonitoringEventTypeType) => (state: RootState) =>
    selectMonitoringEventsByUserIdAndType(userId, eventType)(state).length;

export const selectMonitoringEventCountByTargetUserIdAndType =
  (userId: string, eventType: TargetUserMonitoringEventTypeType) => (state: RootState) =>
    selectMonitoringEvents(state).filter(
      (event) => event.type === eventType && event.contextData.targetUserId === userId,
    ).length;

export const selectAvRefreshCountByUserId = (userId: string) => (state: RootState) => {
  const avRefreshEvents = selectMonitoringEventsByUserIdAndType(
    userId,
    MonitoringEventTypeType.AV_REFRESH,
  )(state) as AvRefreshMonitoringEventType[];
  return avRefreshEvents.filter(filterAvRefreshes).length;
};

export const selectFailedPreClassCheckByUserId = (userId: string) => (state: RootState) => {
  const preclassCheckFailEvents = selectMonitoringEventsByUserIdAndType(
    userId,
    MonitoringEventTypeType.PRECLASS_CHECK_FAIL,
  )(state) as PreClassCheckResultMonitoringEventType[];
  return preclassCheckFailEvents.filter(filterPreclassChecks);
};

export const selectFailedPreClassCheckCountByUserId = (userId: string) => (state: RootState) => {
  const events = selectFailedPreClassCheckByUserId(userId)(state);
  return events.length;
};

export const selectLatestPubsubStatsByUserId = (userId: string) => (state: RootState) => {
  const pubsubStatsEvents = (
    selectMonitoringEventsByUserIdAndType(
      userId,
      MonitoringEventTypeType.PUBSUB_STATS,
    )(state) as PubsubStatsMonitoringEventType[]
  ).filter((event) => (event.contextData.pingRTT?.median || 0) > 0);
  return last(pubsubStatsEvents);
};

export const selectPaginatedMonitoringEvents = () => (state: RootState) => {
  const eventsPerPage = state.local_monitoring.monitoring_event_table_events_per_page;
  const selectedLevels = state.local_monitoring.monitoring_event_table_levels;
  const selectedTypes = state.local_monitoring.monitoring_event_table_types;
  const selectedUsers = state.local_monitoring.monitoring_event_table_users;
  return selectMonitoringEvents(state)
    .filter(filterEvents(selectedLevels, selectedTypes, selectedUsers))
    .splice(0, eventsPerPage);
};

export const selectSupportActions = () => (state: RootState) =>
  selectMonitoringEvents(state).filter((event) =>
    event.type.startsWith("admin"),
  ) as SupportActionMonitoringEventType[];

export const selectSupportActionsExist = () => (state: RootState) =>
  selectSupportActions()(state).length > 0;

export const selectSupportActionCountByUserId = (userId: string) => (state: RootState) =>
  selectSupportActions()(state).filter((event) => event.contextData.targetUserId === userId).length;

export const selectSecondsSinceLastMonitoringEventByUserIdAndType =
  (
    userId: string,
    eventType: MonitoringEventTypeType,
    contextDataKey: contextDataKeyType,
    contextDataValue: contextDataValueType,
  ) =>
  (state: RootState) => {
    const events = selectMonitoringEventsByUserIdAndTypes(userId, [
      MonitoringEventTypeType.LEAVE_CLASS,
      eventType,
    ])(state);
    let seconds = 0;
    let startTime: number | null = null;
    events.forEach((event) => {
      // @ts-expect-error Without knowing the type of event, we can't be sure that `contextData` can be indexed by `contextDataKey`.
      if (event.type === eventType && event.contextData[contextDataKey] !== contextDataValue) {
        startTime = event.timestamp;
      } else if (
        startTime &&
        (event.type === MonitoringEventTypeType.LEAVE_CLASS ||
          // @ts-expect-error Without knowing the type of event, we can't be sure that `contextData` can be indexed by `contextDataKey`.
          (event.type === eventType && event.contextData[contextDataKey] === contextDataValue))
      ) {
        seconds += Math.round((event.timestamp - startTime) / 1000);
        startTime = null;
      }
    });
    return seconds;
  };

export const selectSecondsWithLowQualityByUserId = (userId: string) => (state: RootState) =>
  selectSecondsSinceLastMonitoringEventByUserIdAndType(
    userId,
    MonitoringEventTypeType.AV_CONNECTION_QUALITY_CHANGED,
    "connectionQuality",
    ConnectionQualityLevels.GOOD,
  )(state);

export const selectSecondsWithPoorConnectionIndicatorByUserId =
  (userId: string) => (state: RootState) =>
    selectSecondsSinceLastMonitoringEventByUserIdAndType(
      userId,
      MonitoringEventTypeType.POOR_CONNECTION_INDICATOR_CHANGED,
      "hasPoorConnection",
      false,
    )(state);

export const selectLatestPreClassCheckResultsByUserId =
  (userId: string) =>
  (state: RootState): LatestPreclassCheckResultType => {
    const preclassCheckEvents = selectMonitoringEventsByUserIdAndTypes(userId, [
      MonitoringEventTypeType.PRECLASS_CHECK_SUCCESS,
      MonitoringEventTypeType.PRECLASS_CHECK_FAIL,
    ])(state);
    if (!preclassCheckEvents.length) return { eventId: "", outcome: "", tests: [] };
    const latestPreclassCheckEvent = last(preclassCheckEvents);
    const eventId = latestPreclassCheckEvent?.id || "";
    const outcome =
      latestPreclassCheckEvent?.type === MonitoringEventTypeType.PRECLASS_CHECK_SUCCESS
        ? "success"
        : "fail";
    const tests = Object.values(latestPreclassCheckEvent?.contextData || {}).filter(
      (test) => !!(test as PreclassTestResultType).outcome,
    ) as PreclassTestResultType[];
    return { eventId, outcome, tests };
  };

export const selectAvRTTForUser = (userId: string) => (state: RootState) => {
  const latestAvCheckinEvent = selectLatestMonitoringEventByUserIdAndType(
    userId,
    MonitoringEventTypeType.AV_CHECKIN,
  )(state) as AvCheckinMonitoringEventType;
  if (!latestAvCheckinEvent) return null;
  const { publisherStats } = latestAvCheckinEvent.contextData;
  if (!publisherStats) return null;
  const avStat = Object.values(publisherStats).find((stat) => !!stat.totalRoundTripTime);
  return avStat?.totalRoundTripTime || null;
};

const statsGenerator = (
  state: RootState,
  monitoringEvents: MonitoringEventType[],
  useTargetUserIds = false,
) => {
  const oncePresentUserCount = getOncePresentUserCount(state.classroom.client, state.monitoring);
  return computeMonitoringEventStats(
    state.classroom.client,
    monitoringEvents,
    oncePresentUserCount,
    true, // events are already filtered by time, no need to do it again
    useTargetUserIds,
  );
};

export const selectMonitoringEventStatsByType =
  (eventType: MonitoringEventTypeType, useTargetUserIds = false) =>
  (state: RootState) =>
    statsGenerator(state, selectMonitoringEventsByType(eventType)(state), useTargetUserIds);

export const selectAVRefreshStats = () => (state: RootState) => {
  const monitoringEvents = selectMonitoringEventsByType(MonitoringEventTypeType.AV_REFRESH)(
    state,
  ) as AvRefreshMonitoringEventType[];
  return statsGenerator(state, monitoringEvents.filter(filterAvRefreshes));
};

export const selectFailedPreClassCheckStats = () => (state: RootState) => {
  const monitoringEvents = selectMonitoringEventsByType(
    MonitoringEventTypeType.PRECLASS_CHECK_FAIL,
  )(state) as PreClassCheckResultMonitoringEventType[];
  return statsGenerator(state, monitoringEvents.filter(filterPreclassChecks));
};

export const selectSupportActionStats = () => (state: RootState) => {
  return statsGenerator(state, selectSupportActions()(state), true);
};

export const selectMonitoringEventHistogramData =
  (selectedLevels: string[], selectedTypes: string[], selectedUsers: string[]) =>
  (state: RootState) => {
    const monitoringEvents = selectMonitoringEvents(state).filter(
      filterEvents(selectedLevels, selectedTypes, selectedUsers),
    );
    if (!monitoringEvents.length) return [];
    const timestamps = monitoringEvents.map((event) => event.timestamp);
    const minTime = Math.min(...timestamps);
    const maxTime = Math.max(...timestamps);
    if (minTime === maxTime) return [];
    const nBins = 50;
    const dataArray: MonitoringEventHistogramDataType[] = range(nBins).map((binIndex) => ({
      binMin: binIndex * ((maxTime - minTime) / (nBins - 1)) + minTime,
      binMax: (binIndex + 1) * ((maxTime - minTime) / (nBins - 1)) + minTime,
      nEvents: 0,
    }));
    timestamps.forEach((timestamp) => {
      const binIndex = Math.floor(((timestamp - minTime) / (maxTime - minTime)) * (nBins - 1));
      dataArray[binIndex].nEvents += 1;
    });
    return dataArray;
  };
